import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password?.length >= 8) score++;
  if (password?.length >= 12) score++;
  if (/[A-Z]/?.test(password)) score++;
  if (/[0-9]/?.test(password)) score++;
  if (/[^A-Za-z0-9]/?.test(password)) score++;

  if (score <= 1) return { score, label: 'Muy débil', color: 'bg-red-500' };
  if (score === 2) return { score, label: 'Débil', color: 'bg-orange-500' };
  if (score === 3) return { score, label: 'Regular', color: 'bg-yellow-500' };
  if (score === 4) return { score, label: 'Fuerte', color: 'bg-blue-500' };
  return { score, label: 'Muy fuerte', color: 'bg-green-500' };
};

const AdminAccountForm = ({ formData, errors, onChange }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const strength = getPasswordStrength(formData?.password || '');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
          <Icon name="UserCheck" className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Cuenta de Administrador</h2>
          <p className="text-sm text-gray-500">Credenciales del propietario y administrador principal</p>
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <Icon name="Shield" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Cuenta de Propietario</p>
          <p className="text-xs text-amber-700 mt-0.5">Esta cuenta tendrá acceso completo al sistema y será el administrador principal de tu empresa.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Nombre Completo <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData?.fullName || ''}
            onChange={(e) => onChange('fullName', e?.target?.value)}
            placeholder="Juan García López"
            className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              errors?.fullName ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.fullName && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.fullName}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Correo Electrónico <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData?.email || ''}
            onChange={(e) => onChange('email', e?.target?.value?.toLowerCase())}
            placeholder="admin@mitaller.com"
            className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              errors?.email ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.email && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.email}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Contraseña <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData?.password || ''}
              onChange={(e) => onChange('password', e?.target?.value)}
              placeholder="Mínimo 8 caracteres"
              className={`w-full px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                errors?.password ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} className="w-4 h-4" />
            </button>
          </div>
          {formData?.password && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4, 5]?.map(i => (
                  <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                      i <= strength?.score ? strength?.color : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500">Seguridad: <span className="font-medium">{strength?.label}</span></p>
            </div>
          )}
          {errors?.password && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.password}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Confirmar Contraseña <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData?.confirmPassword || ''}
              onChange={(e) => onChange('confirmPassword', e?.target?.value)}
              placeholder="Repetir contraseña"
              className={`w-full px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                errors?.confirmPassword ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} className="w-4 h-4" />
            </button>
          </div>
          {formData?.confirmPassword && formData?.password === formData?.confirmPassword && (
            <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
              <Icon name="CheckCircle" className="w-3.5 h-3.5" />
              Las contraseñas coinciden
            </p>
          )}
          {errors?.confirmPassword && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.confirmPassword}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Teléfono del Administrador
          </label>
          <input
            type="tel"
            value={formData?.phone || ''}
            onChange={(e) => onChange('phone', e?.target?.value)}
            placeholder="+52 55 1234 5678"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Rol en el Sistema
          </label>
          <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600 flex items-center gap-2">
            <Icon name="Shield" className="w-4 h-4 text-blue-500" />
            Administrador Principal (Propietario)
          </div>
        </div>
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-xs font-medium text-gray-700 mb-2">Requisitos de contraseña:</p>
        <div className="grid grid-cols-2 gap-1.5">
          {[
            { check: (formData?.password?.length || 0) >= 8, text: 'Mínimo 8 caracteres' },
            { check: /[A-Z]/?.test(formData?.password || ''), text: 'Una mayúscula' },
            { check: /[0-9]/?.test(formData?.password || ''), text: 'Un número' },
            { check: /[^A-Za-z0-9]/?.test(formData?.password || ''), text: 'Un carácter especial' }
          ]?.map(req => (
            <div key={req?.text} className="flex items-center gap-1.5 text-xs">
              <Icon
                name={req?.check ? 'CheckCircle' : 'Circle'}
                className={`w-3.5 h-3.5 ${req?.check ? 'text-green-500' : 'text-gray-300'}`}
              />
              <span className={req?.check ? 'text-green-700' : 'text-gray-500'}>{req?.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAccountForm;
